/* eslint-disable no-shadow */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';

import FocusTrap from 'focus-trap-react';
import Button from '../Button';
import ProfileMenuButton from './ProfileMenuButton';
import ProfileMenuLinks from './ProfileMenuLinks';
import ProfileMenuItems from './ProfileMenuItems';
import { getTranslation } from '../../providers/TranslationProvider';
import OPENAI_WIDGET from '../../../../widget/app/modules/constants/srcTypes';

import './ProfileMenu.scss';
import { getUserThunk, selectIsConcierge } from '../../reducers/userSlice';

const ProfileMenu = ({
  Analytics,
  children,
  domain,
  experiments,
  items,
  onLogin,
  onLogout,
  onToggle,
  profileMenuAnalytics,
  showProfileMenu,
  userData,
  isConcierge,
  getUserThunk,
  sourceId,
}) => {
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(undefined);
  const ref = useRef();

  const RESY_DOMAIN = 'resy.com';
  const EVENT_MESSAGE = 'message';
  const EVENT_MOUSEDOWN = 'mousedown';

  const loadUser = async () => {
    try {
      const fetchedUser = await getUserThunk(true);
      setUser(fetchedUser);
    } finally {
      setLoading(false);
    }
  };

  const openToggle = (val) => {
    const value = typeof val === 'boolean' ? val : !open;
    if (onToggle) {
      onToggle(value);
    }
    if (profileMenuAnalytics && value) {
      profileMenuAnalytics();
    }
    setOpen(value);
  };

  const closeMenu = () => {
    openToggle(false);
  };

  const onClickOutside = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      closeMenu();
    }
  };

  const onPostMessage = (e) => {
    if (domain === RESY_DOMAIN && e.data === 'onProfileImage') {
      loadUser();
    }
  };

  useEffect(() => {
    loadUser();
    if (domain === RESY_DOMAIN) {
      window.addEventListener(EVENT_MESSAGE, onPostMessage);
    }
    document.addEventListener(EVENT_MOUSEDOWN, onClickOutside);

    return () => {
      if (domain === RESY_DOMAIN) {
        window.removeEventListener(EVENT_MESSAGE, onPostMessage);
      }
      document.removeEventListener(EVENT_MOUSEDOWN, onClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!userData && user) {
      setUser(null);
    } else if (userData && !user) {
      setUser(userData);
    }
  }, [userData]);

  if (sourceId === OPENAI_WIDGET) {
    return null;
  }

  return (
    <div className="ProfileMenu" ref={ref}>
      <div className="ProfileMenu__CTA-container">
        <div className="ProfileMenu__CTA">
          <ProfileMenuButton
            disabled={!open}
            loading={loading}
            onLogin={onLogin}
            onProfile={openToggle}
            open={open}
            sourceId={sourceId}
            user={showProfileMenu && user}
          />
        </div>
        {children}
      </div>

      {showProfileMenu && user && (
        <FocusTrap
          active={open}
          focusTrapOptions={{
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
          }}
        >
          <div className={classnames('ProfileMenu__Menu', { open })} aria-hidden={!open}>
            <ProfileMenuLinks
              Analytics={Analytics}
              disabled={!open}
              isConcierge={isConcierge}
              items={items}
              onLogout={onLogout}
              onToggle={closeMenu}
              experiments={experiments}
            />

            <Button
              aria-label={getTranslation({ translationKey: 'close_profile_menu', fallback: 'close profile menu' })}
              data-test-id="profile_menu-button-close"
              disabled={!open}
              onClick={openToggle}
              variant="close"
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

ProfileMenu.propTypes = {
  Analytics: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  domain: PropTypes.string,
  experiments: PropTypes.shape({}).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      path: PropTypes.string,
      translationKey: PropTypes.string,
      activeMatcher: PropTypes.string,
    })
  ),
  onLogin: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  onToggle: PropTypes.func,
  profileMenuAnalytics: PropTypes.func,
  showProfileMenu: PropTypes.bool,
  userData: PropTypes.shape({}).isRequired,
  isConcierge: PropTypes.bool.isRequired,
  getUserThunk: PropTypes.func.isRequired,
  sourceId: PropTypes.string,
};

ProfileMenu.defaultProps = {
  Analytics: {},
  children: null,
  domain: null,
  items: ProfileMenuItems,
  onToggle: () => {},
  profileMenuAnalytics: () => {},
  showProfileMenu: false,
  sourceId: undefined,
};

const mapStateToProps = (state) => ({
  userData: state.user.userData,
  isConcierge: selectIsConcierge(state),
  experiments: state.experiments,
});
const mapDispatchToProps = { getUserThunk };

export { ProfileMenu as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(ProfileMenu);
